import React, { useEffect } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { Map } from 'immutable';

import { fetchCallRecordWithEntityKey } from 'flow/actions/extension-actions';
import ContactContainer from 'flow/components/contact-card/ContactContainers';
import Loading from 'flow/components/ui-components/Loading';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { selectCallRecordFromKey } from 'flow/selectors/call-selectors';
import { getContact } from 'flow/selectors/contact-selectors';
import { CallParams } from 'flow/types/CallParams';
import { ContactKey, ExtensionKey } from 'flow/types/EntityKeys';
import { routes } from 'flow/types/routes';
import { generatePath } from 'flow/utils/router-utils';

type Params = CallParams & {
    // Set on the PBX queue routes only
    entityKey?: string;
    bPart?: string;
};

const QUEUE_CONTACT_ROUTES = [
    routes['pbx.queues.entityKey.number.callRecordKey.contact'],
    routes['pbx.queues.entityKey.number.callRecordKey.contactKey'],
];

// The calendar next to this call record, in call history or the PBX queue.
// A contact without a key gets no path; the schedule card then falls back to
// the calendar under Contacts, as in CallRecordContactModal.
const getCalendarPath = (
    path: string,
    { callRecordKey, entityKey, bPart }: Params,
    contactKey: ContactKey | undefined,
): string | undefined => {
    if (!contactKey) {
        return undefined;
    }

    return QUEUE_CONTACT_ROUTES.includes(path)
        ? generatePath(
              routes[
                  'pbx.queues.entityKey.number.callRecordKey.contactKey.calendar'
              ],
              { entityKey, bPart, callRecordKey, contactKey },
          )
        : generatePath(routes['calls.callRecordKey.contactKey.calendar'], {
              callRecordKey,
              contactKey,
          });
};

const CallContactManagement = () => {
    const dispatch = useAppDispatch();
    const { path, params } = useRouteMatch<Params>();
    const { callRecordKey } = params;

    const callRecord = useAppSelector(
        (state) => selectCallRecordFromKey(state, callRecordKey) || Map(),
    );

    const contactKey = callRecord.getIn(['contact', 'key']);

    const contact = useAppSelector((state) =>
        contactKey
            ? getContact(state, contactKey) || callRecord.get('contact')
            : callRecord.get('contact'),
    );

    const extensionKey = useAppSelector(
        (state) =>
            (
                state.extensions
                    .get('extensions')
                    .find(
                        (extension) =>
                            extension.getIn(['contact', 'key']) === contactKey,
                    ) || Map()
            ).get('key') as ExtensionKey,
    );

    useEffect(() => {
        if (callRecord.isEmpty()) {
            dispatch(fetchCallRecordWithEntityKey(callRecordKey));
        }
    }, [contact]);

    if (callRecord.isEmpty() || !contact) {
        return <Loading />;
    }

    return (
        <ContactContainer
            parentContactKey={contact.get('key')}
            parentExtensionKey={extensionKey}
            callRecordKey={callRecordKey}
            calendarPath={getCalendarPath(path, params, contact.get('key'))}
        />
    );
};

export default CallContactManagement;
